/**
 * GalleryPreviewDialog — preview a gallery item + add-to-dashboard select.
 * Phase 6 extract from pages/Gallery.jsx.
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { BarChart2, UserPlus } from "lucide-react";
import { KpiWidget, ChartWidget, TableWidget } from "@/components/dashboards/Widgets";
import { galleryItemToWidget } from "@/components/dashboards/GalleryPickerDialog";
import { KIND_ICON } from "@/components/gallery/GalleryListView";

// Preview → render registry. Each kind picks a renderer that consumes a
// Dashboard-widget shape (`galleryItemToWidget(item)`).
function GalleryPreview({ item }) {
    if (!item) return null;
    const w = galleryItemToWidget(item);
    if (!w) return <div className="text-xs text-muted-foreground font-mono">Unsupported kind: {item.kind}</div>;
    // iter-119al — formula-based widgets (chart/kpi/table built from a
    // `formula` + model `group_by`) need server-side aggregation to render
    // any data; they cannot be previewed in isolation because there is no
    // dashboard / scope context here. Show a clear "live data on add"
    // placeholder instead of an empty chart frame.
    const needsLive = !!(w.formula || (Array.isArray(w.kpis) && w.kpis.length));
    const hasInlineData = !!(w.config && (
        (Array.isArray(w.config.data) && w.config.data.length) ||
        (Array.isArray(w.config.rows) && w.config.rows.length) ||
        w.config.value !== undefined
    ));
    if (needsLive && !hasInlineData) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-center gap-2 py-6">
                <div className="eyebrow text-[10px]">{w.title}</div>
                <div className="text-xs text-muted-foreground max-w-sm">
                    Live preview is unavailable for prebuilt widgets in the Gallery — they aggregate against your live tenant data. Add this to a dashboard to see real numbers.
                </div>
                <div className="text-[10px] font-mono text-muted-foreground/70 mt-2">
                    <span className="uppercase tracking-wider">Source</span> · {(item.data_model_bindings || [])
                        .map((b) => (typeof b === "string" ? b : b?.model_id))
                        .filter(Boolean)
                        .join(", ") || "—"}
                </div>
            </div>
        );
    }
    if (w.type === "kpi_card") return <KpiWidget w={w} />;
    // iter-121.5 — Recharts ResponsiveContainer uses height="100%", so the
    // parent MUST have an explicit computed height (a CSS `min-h` only
    // sets a floor — percentage children still resolve to 0 in flex).
    // Wrap chart/table renderers in a fixed-height frame; KPI / Insight /
    // empty-state keep their natural flow so they don't get bottom-heavy.
    if (w.type === "chart")    return <div className="h-[320px]"><ChartWidget w={w} /></div>;
    if (w.type === "table")    return <div className="h-[320px]"><TableWidget w={w} /></div>;
    return <div className="text-xs text-muted-foreground font-mono">No preview available.</div>;
}

export default function GalleryPreviewDialog({ ctx }) {
    const { preview, setPreview, dashboards, addToDashboard, canShare, setShareItem } = ctx;
    return (
        <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
            <DialogContent className="sm:max-w-2xl" data-testid="gallery-preview-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {preview && (React.createElement(KIND_ICON[preview.kind] || BarChart2, { className: "h-4 w-4" }))}
                        <span>{preview?.name}</span>
                        <Badge variant="outline" className="font-mono text-[10px] uppercase ml-2">{preview?.kind}</Badge>
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    {preview?.description && (
                        <div className="text-xs text-muted-foreground">{preview.description}</div>
                    )}
                    <div className="border border-border bg-card rounded-sm p-4 min-h-[220px]" data-testid="gallery-preview-body">
                        <GalleryPreview item={preview} />
                    </div>
                    {preview?.kind === "insight" && preview?.config?.structured?.kpis && (
                        <div className="grid grid-cols-2 gap-2" data-testid="gallery-preview-insight-kpis">
                            {preview.config.structured.kpis.slice(0, 4).map((k, idx) => (
                                <div key={idx} className="border border-border bg-secondary/20 rounded-sm p-3">
                                    <div className="eyebrow text-[10px]">{k.label}</div>
                                    <div className="text-xl font-medium mt-1">{k.value}{k.unit || ""}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex items-center gap-2 pt-2 border-t border-border">
                        <select
                            defaultValue=""
                            onChange={(e) => addToDashboard(e.target.value)}
                            className="h-9 px-3 bg-background border border-border rounded-sm text-xs flex-1"
                            data-testid="gallery-preview-add-select">
                            <option value="" disabled>Add to dashboard…</option>
                            {dashboards.map((d) => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                            ))}
                        </select>
                        {preview && canShare(preview) && (
                            <Button variant="outline" size="sm" onClick={() => setShareItem(preview)} data-testid="gallery-preview-share">
                                <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Share
                            </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => setPreview(null)} data-testid="gallery-preview-close">Close</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
